import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { withRouter } from 'react-router';
import { setData } from 'platform/forms-system/src/js/actions';
import { getFormData } from 'platform/forms-system/src/js/state/selectors';
import { buildConflicts, resolveFieldGroup } from '../domain/conflictDetection';
import {
  RESOLUTIONS_KEY,
  ORIGINALS_KEY,
  getActiveResolution,
} from '../domain/conflictResolution';
import CaveErrorBoundary from './CaveErrorBoundary';

// Build a map of trackingKey → enumerated label (e.g. "DD-214 (2)") for doc
// types that appear more than once across all files.
const buildDocIndexMap = files => {
  const counters = {};
  const map = {};
  (files ?? []).forEach(file => {
    const key = file.idpTrackingKey;
    if (!key) return;
    const types = [];
    if (file.idpArtifacts?.dd214?.length) types.push('DD-214');
    if (file.idpArtifacts?.deathCertificates?.length)
      types.push('death certificate');
    types.forEach(t => {
      counters[t] = (counters[t] || 0) + 1;
      map[`${t}::${key}`] = counters[t];
    });
  });
  // Only enumerate if there are multiple of a given type
  const totals = counters;
  return { map, totals };
};

const formatSourceLabel = (sf, map, totals) => {
  const total = totals[sf.docTypeLabel] || 1;
  if (total > 1) {
    const idx = map[`${sf.docTypeLabel}::${sf.trackingKey}`];
    return idx !== undefined ? `${sf.docTypeLabel} (${idx})` : sf.docTypeLabel;
  }
  return sf.docTypeLabel;
};

const joinSourceLabels = labels => {
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
};

const ConflictCard = ({
  conflict,
  formDisplayValue,
  resolution,
  onResolutionChange,
  docIndexMap,
  rootUrl,
  router,
}) => {
  const radioRef = useRef(null);
  // Ref keeps the handler current without re-registering the listener.
  const onResolutionChangeRef = useRef(onResolutionChange);
  onResolutionChangeRef.current = onResolutionChange;

  useEffect(() => {
    const el = radioRef.current;
    if (!el) return undefined;
    const handler = e => {
      onResolutionChangeRef.current(conflict.id, e.detail.value);
    };
    el.addEventListener('vaValueChange', handler);
    return () => el.removeEventListener('vaValueChange', handler);
  }, [conflict.id]);

  return (
    <div className="vads-u-margin-bottom--4">
      <va-radio ref={radioRef} label={conflict.label} required uswds>
        <va-radio-option
          name={`conflict-${conflict.id}`}
          label={`${formDisplayValue} (entered by you)`}
          value="form"
          checked={resolution === 'form'}
          tile
          class="dd-privacy-mask"
          data-dd-action-name="Conflict option: your entry"
        />
        {conflict.artifactOptions.map(option => {
          const sourceLabels = option.sourceFiles.map(sf =>
            formatSourceLabel(sf, docIndexMap.map, docIndexMap.totals),
          );
          return (
            <va-radio-option
              key={option.norm}
              name={`conflict-${conflict.id}`}
              label={`${option.displayValue} (found in ${joinSourceLabels(
                sourceLabels,
              )})`}
              value={option.norm}
              checked={resolution === option.norm}
              tile
              class="dd-privacy-mask"
              data-dd-action-name="Conflict option: document value"
            />
          );
        })}
      </va-radio>
      <p className="vads-u-margin-top--1">
        <va-link
          href={`${rootUrl}/${conflict.editPath}`}
          text={`Edit ${conflict.label}`}
          data-dd-action-name="Edit Conflict Field"
          onClick={e => {
            e.preventDefault();
            router.push(`/${conflict.editPath}`);
          }}
        />
      </p>
    </div>
  );
};

ConflictCard.propTypes = {
  conflict: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    editPath: PropTypes.string.isRequired,
    artifactOptions: PropTypes.arrayOf(
      PropTypes.shape({
        norm: PropTypes.string,
        displayValue: PropTypes.string,
        sourceFiles: PropTypes.arrayOf(
          PropTypes.shape({
            docTypeLabel: PropTypes.string,
            fileName: PropTypes.string,
            trackingKey: PropTypes.string,
          }),
        ),
        rawValue: PropTypes.any,
      }),
    ),
  }).isRequired,
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  onResolutionChange: PropTypes.func.isRequired,
  docIndexMap: PropTypes.object,
  formDisplayValue: PropTypes.string,
  resolution: PropTypes.string,
  rootUrl: PropTypes.string,
};

/**
 * Factory that returns a `ui:field` component bound to a specific fieldGroup.
 * @param {string} groupKey - 'veteranInfo' or 'militaryHistory'
 * @param {string} emptyMessage - message when no conflicts are found
 */
const createConflictPageField = (groupKey, emptyMessage) => {
  const fieldGroup = resolveFieldGroup(groupKey);
  const InnerField = ({ router }) => {
    const rootUrl =
      (router?.routes || []).find(r => r?.formConfig?.rootUrl)?.formConfig
        ?.rootUrl || '';
    const dispatch = useDispatch();
    const store = useStore();
    const formData = useSelector(getFormData) || {};

    // Conflicts and their options are a pure function of the form data and
    // uploaded files, recomputed live each render; the user's selection comes
    // from the persisted resolution markers.
    const conflicts = buildConflicts(
      formData,
      formData.files ?? [],
      fieldGroup,
      groupKey,
    );

    // Write-through: the resolved value is written into the real form field (so
    // the review page and submission are correct), while the selection marker
    // and captured original are persisted under `view:` keys so the radios
    // re-hydrate and the choice can be reverted.
    const handleResolutionChange = (fieldId, choice) => {
      const current = store.getState().form.data ?? {};
      const liveConflicts = buildConflicts(
        current,
        current.files ?? [],
        fieldGroup,
        groupKey,
      );
      const conflict = liveConflicts.find(c => c.id === fieldId);
      const fieldDef = fieldGroup.find(f => f.id === fieldId);
      if (!conflict || !fieldDef) return;

      const groupResolutions = {
        ...(current[RESOLUTIONS_KEY]?.[groupKey] ?? {}),
      };
      const groupOriginals = { ...(current[ORIGINALS_KEY]?.[groupKey] ?? {}) };

      let nextData;
      if (choice === 'form') {
        // Revert: restore the user's own value and drop the captured original.
        nextData = fieldDef.applyToForm(current, conflict.formValue);
        groupResolutions[fieldId] = 'form';
        delete groupOriginals[fieldId];
      } else {
        const option = conflict.artifactOptions.find(o => o.norm === choice);
        if (!option) return;
        // Capture the user's value (conflict.formValue is the original once a
        // selection is active) so the choice can be reverted later.
        nextData = fieldDef.applyToForm(current, option.rawValue);
        groupResolutions[fieldId] = choice;
        groupOriginals[fieldId] = conflict.formValue;
      }

      dispatch(
        setData({
          ...nextData,
          [RESOLUTIONS_KEY]: {
            ...(nextData[RESOLUTIONS_KEY] ?? {}),
            [groupKey]: groupResolutions,
          },
          [ORIGINALS_KEY]: {
            ...(nextData[ORIGINALS_KEY] ?? {}),
            [groupKey]: groupOriginals,
          },
        }),
      );
    };

    const docIndexMap = buildDocIndexMap(formData.files);

    if (!conflicts.length) {
      return <p>{emptyMessage}</p>;
    }

    return (
      <>
        {conflicts.map(conflict => {
          const fieldDef = fieldGroup.find(f => f.id === conflict.id);
          // "Entered by you" shows the user's own value (conflict.formValue),
          // which is the original entry even after a scanned option was picked.
          const formDisplayValue =
            fieldDef?.formatValue(conflict.formValue) ?? '';
          return (
            <ConflictCard
              key={conflict.id}
              conflict={conflict}
              formDisplayValue={formDisplayValue}
              resolution={getActiveResolution(formData, fieldDef, groupKey)}
              onResolutionChange={handleResolutionChange}
              docIndexMap={docIndexMap}
              rootUrl={rootUrl}
              router={router}
            />
          );
        })}
      </>
    );
  };
  InnerField.propTypes = {
    router: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
  };
  const InnerFieldWithRouter = withRouter(InnerField);
  return () => (
    <CaveErrorBoundary>
      <InnerFieldWithRouter />
    </CaveErrorBoundary>
  );
};

export default createConflictPageField;

export const createVeteranInfoConflictField = emptyMessage =>
  createConflictPageField('veteranInfo', emptyMessage);

export const createMilitaryHistoryConflictField = emptyMessage =>
  createConflictPageField('militaryHistory', emptyMessage);
